import React, { useCallback, useEffect, useState } from "react";
import { Typography, Button, Box, List, ListItem } from "@mui/material";
import { CartType } from "../../models/CartType";
import { useSignedInUserState } from "../../hooks/useAuthState";
import { useNavigate, useParams } from "react-router-dom";
import { OrderHistoryService } from "../../services/order/orderHistoryService";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Lottie from "lottie-react";
import { viewHightMinusHeader } from "../../header";
import { CartService } from "../../services/functions/cartService";
import AsyncString from "../../shared/asyncString";
import { FullProperty } from "../../models/fullProperty";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { StringsService } from "../../services/strings/strings";


type OrderFetchStatus = "loading" | "not_found" | "confirmed" | "needs_collection";

const OrderErrorView = ({ onBack, animation }: { onBack: () => void; animation: any }) => {
    return (
        <Box
            sx={{
                minHeight: viewHightMinusHeader,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                paddingX: 2,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    boxShadow: 3,
                    borderRadius: 2,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 800,
                    transform: "translateY(100px) scale(0.95)",
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },
                }}
            >
                {animation ? (
                    <Lottie animationData={animation} style={{ width: 200, height: 200 }} />
                ) : (
                    <ErrorOutlineIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
                )}

                <Typography
                    variant="h6"
                    textAlign="center"
                    sx={{
                        fontWeight: "bold",
                        marginTop: 2,
                        color: "error.main",
                        maxWidth: 600,
                    }}
                >
                    Oops! We couldn't find your order.
                </Typography>

                <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{
                        marginTop: 1,
                        maxWidth: 500,
                        color: "text.secondary",
                    }}
                >
                    There was an issue retrieving your order details. Please check your
                    network connection, go back and try again.
                </Typography>

                <Button
                    variant="contained"
                    color="error"
                    sx={{
                        mt: 4,
                        borderRadius: "24px",
                        width: "100%",
                        textTransform: "none",
                    }}
                    onClick={onBack}
                >
                    Back
                </Button>
            </Box>
        </Box>
    );
};

const OrderLoadingView = ({ animation }: { animation: any }) => {
    return (
        <Box
            sx={{
                minHeight: viewHightMinusHeader,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                paddingX: 2,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    boxShadow: 3,
                    borderRadius: 2,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 800,
                    transform: "translateY(100px) scale(0.95)",
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },
                }}
            >
                <Lottie animationData={animation} style={{ width: 200, height: 200 }} />

                <Typography
                    variant="h6"
                    textAlign={"center"}
                    sx={{
                        fontWeight: "bold",
                        marginTop: 4,
                        color: "#162C1F",
                        maxWidth: 600
                    }}
                >
                    Loading your conscious order...
                </Typography>

                <Typography
                    variant="body2"
                    textAlign={"center"}
                    sx={{
                        marginTop: 2,
                        maxWidth: 500,
                        color: "text.secondary"
                    }}
                >
                    Good food for a better planet. 🌱
                </Typography>
            </Box>
        </Box>
    );
}

const OrderCollected = ({ animation, order, restaurant, cartItems }: { cartItems: string[]; animation: any; order: CartType, restaurant: FullProperty }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: viewHightMinusHeader,
                textAlign: "center",
                paddingX: 2,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    padding: 4,
                    borderRadius: 4,
                    boxShadow: 3,
                    maxWidth: 800,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    opacity: 0,
                    transform: "translateY(100px) scale(0.95)",
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },
                }}
            >
                <Lottie animationData={animation} style={{ width: "100%", height: 200 }} />

                <Typography variant="h4" sx={{ marginBottom: 2 }}>
                    <List>
                        {
                            cartItems.map(x => <ListItem sx={{ display: 'list-item' }}>{x}</ListItem>)
                        }
                    </List>
                </Typography>


                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                    <AsyncString stringId={order.restaurantId + '-restaurant-name'} />
                </Typography>

                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                    {restaurant.addressName}
                </Typography>

                <Typography variant="body2" sx={{ marginBottom: 1, color: "text.secondary" }}>
                    Please show this to the restaurant staff. Enjoy your meal!
                </Typography>

                <Typography variant="body1" sx={{ marginTop: 3 }}>
                    Thank you for using Eco Eats 💚
                </Typography>
            </Box>
        </Box>
    );
}

const ConfirmCollectOrderNow = ({ order, restaurant, onCollect }: { order: CartType; restaurant: FullProperty; onCollect: () => void }) => {
    return (
        <Box
            sx={{
                minHeight: viewHightMinusHeader,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                paddingX: 2,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    boxShadow: 3,
                    borderRadius: 2,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 800,
                    transform: "translateY(100px) scale(0.95)",
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },
                }}
            >

                <Typography
                    variant="h4"
                    textAlign="center"
                    sx={{
                        fontWeight: "bold",
                        marginTop: 2,
                        color: "#162C1F",
                        maxWidth: 600,
                    }}
                >
                    Collect Your Order
                </Typography>

                <Typography variant="h5" sx={{ marginTop: 2 }}>
                    <AsyncString stringId={order.restaurantId + '-restaurant-name'} />
                </Typography>

                <Typography
                    textAlign="center"
                    variant="body2"
                    sx={{ marginTop: 2 }}>
                    {restaurant.addressName}
                </Typography>

                <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{
                        marginTop: 2,
                        maxWidth: 500,
                        color: "text.secondary",
                    }}
                >
                    Tap below to let Eco Eats know you've arrived. You'll see a confirmation page with your order details to show the staff.
                </Typography>

                <Button
                    variant="contained"
                    sx={{
                        mt: 4,
                        background: "#162C1F",
                        borderRadius: "24px",
                        width: "100%",
                        textTransform: "none",
                        "&:hover": { background: "#131F18" },
                    }}
                    onClick={onCollect}
                >
                    <Typography variant="button" component="span" sx={{ color: "#fff" }}>
                        Confirm
                    </Typography>
                </Button>
            </Box>
        </Box>
    );
}

export const OrderCollectPage = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const { profile } = useSignedInUserState();
    const navigate = useNavigate()
    const [restaurant, setRestaurant] = useState<FullProperty | null>(null)
    const [order, setOrder] = useState<CartType | null>(null)
    const [status, setStatus] = useState<OrderFetchStatus>("loading");
    const [cartItemsLabel, setCartItemsLabel] = useState<string[]>([])

    const animationPath = "/animation/common_loading_animation.json";
    const [animationData, setAnimationData] = useState<any>(null);

    const animationSuccessPath = "/animation/collect_order_animation.json";
    const [animationSuccessData, setAnimationSuccessData] = useState<any>(null);


    const nothinHereAnimationPath = "/animation/nothing_here_animation.json";
    const [nothingHereAnimationData, setNothingHereAnimationData] = useState<any>(null);


    useEffect(() => {
        fetch(animationSuccessPath)
            .then((response) => response.json())
            .then((data) => setAnimationSuccessData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationSuccessPath]);

    useEffect(() => {
        fetch(animationPath)
            .then((response) => response.json())
            .then((data) => setAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    useEffect(() => {
        fetch(nothinHereAnimationPath)
            .then((response) => response.json())
            .then((data) => setNothingHereAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [nothinHereAnimationPath]);

    const loadOrder = async () => {
        setStatus("loading");

        if (!orderId) {
            setOrder(null);
            setStatus("not_found");
            return;
        }

        try {
            const data = await OrderHistoryService.getOrderById(orderId);

            if (!data) {
                setOrder(null);
                setStatus("not_found");
            } else {
                setOrder(data);
                const rest = await restaurantService.fetchRestaurantById(data.restaurantId)
                if (rest) {
                    setRestaurant(rest)
                } else {
                    throw new Error("restaurant missing")
                }
                if (data.orderCollected) {
                    setStatus("confirmed");
                    let newCartItemsLabels: string[] = []
                    const promises = data.items.map(async (element) => {
                        newCartItemsLabels.push(`${element.quantity}X - ${element.menuCartItem.name}`)
                    });
                    await Promise.all(promises)
                    setCartItemsLabel(newCartItemsLabels)
                } else {
                    setStatus("needs_collection");
                }
            }
        } catch (err) {
            setOrder(null);
            setStatus("not_found");
        }
    }

    useEffect(() => {
        loadOrder();
    }, []);

    const onCollect = async () => {
        if (!order || !order.orderCollectionNumber) {
            return;
        }

        setStatus("loading");

        try {
            await CartService.collectOrder({
                orderNumberId: order.orderCollectionNumber,
                restaurantId: order.restaurantId,
            });
            await loadOrder();
        } catch (err) {
            setStatus("not_found");
        }
    };

    if (status === "loading") {
        return <OrderLoadingView animation={animationData} />;
    }

    if (status === "not_found" || !order || !restaurant) {
        return (
            <OrderErrorView
                animation={nothingHereAnimationData}
                onBack={() => navigate("/order/history")}
            />
        );
    }

    if (status === "confirmed" && order) {
        return <OrderCollected cartItems={cartItemsLabel} animation={animationSuccessData} order={order} restaurant={restaurant} />;
    }

    return <ConfirmCollectOrderNow order={order} restaurant={restaurant} onCollect={onCollect} />;
};
